import { useState } from 'react';
import { Stack, Text, Button, Paper } from '@mantine/core';
import { IconPlus, IconEdit } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';
import { Patient, Encounter } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react';
import { PhotosListItem } from './PhotosListItem';
import { PhotosInputModal } from './PhotosInputModal';
import type { PhotoFile } from './PhotoInput';
import {
  savePhotos,
  loadEncounterPhotos,
} from '@/pages/ainigma/patient/examination/examination.helpers';
import { notifications } from '@mantine/notifications';

export interface PhotosListProps {
  photos: PhotoFile[];
  onChange: (photos: PhotoFile[]) => void;
  patient: Patient;
  encounter: Encounter;
  maxPhotos?: number;
  readonly?: boolean;
}

export const PhotosList = ({
  photos,
  onChange,
  patient,
  encounter,
  maxPhotos = 5,
  readonly = false,
}: PhotosListProps) => {
  const { t } = useTranslation(['patient-pages']);
  const medplum = useMedplum();

  // Modal states
  const [modalOpened, setModalOpened] = useState(false);
  const [modalMode, setModalMode] = useState<'add' | 'edit'>('add');

  // Check if we have any data
  const hasData = photos.length > 0;

  // Handlers
  const handleAdd = () => {
    setModalMode('add');
    setModalOpened(true);
  };

  const handleRemovePhoto = async (index: number) => {
    const photoToDelete = photos[index];

    if (!photoToDelete?.id) {
      // If photo doesn't have an ID, just remove from local state
      const updated = photos.filter((_, i) => i !== index);
      onChange(updated);
      return;
    }

    try {
      // Delete from server immediately
      const remainingPhotos = photos.filter((_, i) => i !== index);
      await savePhotos(medplum, patient, encounter, remainingPhotos);

      // Invalidate cache
      medplum.invalidateSearches('Media');

      // Reload from server
      const { photos: reloadedPhotos } = await loadEncounterPhotos(
        medplum,
        patient,
        encounter
      );
      onChange(reloadedPhotos);

      // Show success notification
      notifications.show({
        color: 'green',
        title: t('photos.deleteSuccess', {
          ns: 'patient-pages',
          defaultValue: 'Deleted',
        }),
        message: t('photos.deleteSuccessMessage', {
          ns: 'patient-pages',
          defaultValue: 'Photo deleted successfully',
        }),
      });
    } catch (_error) {
      notifications.show({
        color: 'red',
        title: t('photos.deleteError', {
          ns: 'patient-pages',
          defaultValue: 'Error',
        }),
        message: t('photos.deleteErrorMessage', {
          ns: 'patient-pages',
          defaultValue: 'Failed to delete photo',
        }),
      });
    }
  };

  const handleSave = async (savedPhotos: PhotoFile[]) => {
    // Save immediately to the server
    await savePhotos(medplum, patient, encounter, savedPhotos);

    // Invalidate cache to force fresh fetches (standard pattern in codebase)
    medplum.invalidateSearches('Media');
  };

  const handleSaveSuccess = async (savedPhotos: PhotoFile[]) => {
    // Reload the data from the server to get the updated photos with IDs
    try {
      const { photos: reloadedPhotos } = await loadEncounterPhotos(
        medplum,
        patient,
        encounter
      );
      onChange(reloadedPhotos);

      // Show success notification
      notifications.show({
        color: 'green',
        title: t('photos.saveSuccess', {
          ns: 'patient-pages',
          defaultValue: 'Saved',
        }),
        message: t('photos.saveSuccessMessage', {
          ns: 'patient-pages',
          defaultValue: 'Photos saved successfully',
        }),
      });
    } catch (_error) {
      // Even if reload fails, update with the saved photos
      onChange(savedPhotos);
    }
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Text size="xl" fw={500}>
        {t('examination.images.photos', {
          ns: 'patient-pages',
          defaultValue: 'Photos',
        })}{' '}
        ({photos.length}/{maxPhotos})
      </Text>

      {/* Empty state or display photos */}
      {!hasData ? (
        <>
          <Paper withBorder radius="md" p="xl" bg="gray.0">
            <Text size="sm" c="dimmed" ta="center">
              {t('photos.noPhotos', {
                ns: 'patient-pages',
                defaultValue: 'No photos recorded',
              })}
            </Text>
          </Paper>
          {!readonly && (
            <Button
              leftSection={<IconPlus size={16} />}
              variant="light"
              size="sm"
              onClick={handleAdd}
            >
              {t('photos.addPhotos', {
                ns: 'patient-pages',
                defaultValue: 'Add Photos',
              })}
            </Button>
          )}
        </>
      ) : (
        <>
          {/* Photos display */}
          <PhotosListItem
            photos={photos}
            onAction={handleRemovePhoto}
            readonly={readonly}
          />

          {/* Edit button */}
          {!readonly && (
            <Button
              leftSection={<IconEdit size={16} />}
              variant="light"
              size="sm"
              onClick={handleAdd}
            >
              {t('photos.managePhotos', {
                ns: 'patient-pages',
                defaultValue: 'Manage Photos',
              })}
            </Button>
          )}
        </>
      )}

      {/* Modal */}
      <PhotosInputModal
        opened={modalOpened}
        onClose={() => setModalOpened(false)}
        onSave={handleSave}
        onSaveSuccess={handleSaveSuccess}
        photos={hasData ? photos : undefined}
        mode={modalMode}
        maxPhotos={maxPhotos}
      />
    </Stack>
  );
};
